import { isFluid } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Fluid — rates are set at the shared LIQUIDITY LAYER, not at the vault.
 *
 * `utilization` on a Fluid row is the layer's utilization for that token, so a
 * rate simulation must shift `irmTotal*`, never the row totals. Marking the
 * basis is the difference between a correct impact estimate and a badly wrong
 * one.
 */
export const fluidAdapter: TermAdapter = {
  id: 'fluid',
  matches: isFluid,
  profileId: () => 'pool.variable@v1',
  build: (input) => {
    const smart = (input.market?.fluid ?? {}) as Record<string, any>
    const isSmartCol = !!smart.isSmartCol
    const isSmartDebt = !!smart.isSmartDebt
    const isSmart = isSmartCol || isSmartDebt

    const pair = (p: unknown) =>
      Array.isArray(p) ? (p as string[]).join(' + ') : undefined

    return {
      // Only claim the liquidity-layer basis when the layer totals are actually
      // present. The origin does not select `irmTotal*` today, and asserting a
      // basis we cannot verify is worse than reporting the plain market ratio —
      // the validator rejects it for exactly that reason.
      utilization:
        input.irmTotalDeposits != null && input.irmTotalDebt != null
          ? { basis: 'liquidity-layer', lockupRatio: input.lockupRatio }
          : { lockupRatio: input.lockupRatio },
      constraints: {
        positionModel: 'nft',
        positionIdMeaning: 'The Fluid vault position NFT id.',
        ...(isSmart
          ? {
              /**
               * A smart vault is NOT a plain pool, and the difference is what a
               * user is actually agreeing to:
               *
               *  - the position is a two-token DEX LP share, so its value moves
               *    with the pool's composition, not just with the two prices;
               *  - the row's amount is this leg only — the other leg is a
               *    sibling row of the same vault, and neither can be moved
               *    alone;
               *  - exiting means unwinding LP shares, which is bounded by
               *    share-slippage parameters rather than a plain transfer.
               */
              notes: [
                isSmartCol
                  ? `Collateral is a Fluid DEX LP position over ${pair(smart.collateralPair) ?? 'two tokens'}; this row is one leg of it.`
                  : undefined,
                isSmartDebt
                  ? `Debt is a Fluid DEX LP position over ${pair(smart.debtPair) ?? 'two tokens'}; this row is one leg of it.`
                  : undefined,
                isSmartDebt
                  ? 'The borrow rate is NET of the trading yield the debt LP earns, so it can be negative.'
                  : undefined,
                isSmartCol
                  ? 'The supply rate INCLUDES the DEX trading yield on top of the liquidity-layer rate.'
                  : undefined,
                'Both legs of a side move together — a leg cannot be supplied, borrowed or withdrawn on its own.',
              ].filter(Boolean) as string[],
            }
          : {}),
      },
    }
  },
}
